import { useJsApiLoader } from '@react-google-maps/api'
import CustomDialog from 'common/components/CustomDialog'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { googleMapsConfig } from 'googleMapsConfig'
import { useFarms } from 'modules/farm/api/queries'
import { useFarmsReverseGeocode } from 'modules/farm/api/queries'
import { useMutateCreateWarehouse, useMutateEditWarehouse } from 'modules/inventory/api/mutation'
import type { CreateWarehouseBody, Warehouse } from 'modules/inventory/api/type'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'

import LocationMap from 'modules/inventory/components/GoogleMap'

interface CreateWarehouseDialogProps {
  open: boolean
  onClose: () => void
  warehouse?: Warehouse
}

export default function CreateWarehouseDialog({ open, onClose, warehouse }: CreateWarehouseDialogProps) {
  const { t } = useTranslation('warehouse')
  const isEdit = Boolean(warehouse)
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)

  const schema = Yup.object().shape({
    name: Yup.string().required(t('please.input.name')),
  })
  const resolver = useYupValidationResolver(schema)

  const { control, setValue, reset, handleSubmit, formState: { errors } } = useForm<CreateWarehouseBody>({
    defaultValues: {
      name: '', farm_id: '', full_address: '',
      lat: null, lng: null,
      province: '', district: '', subdistrict: '', postal_code: '',
    },
    resolver,
  })

  const { data: farmsData } = useFarms()
  const farms = React.useMemo(() => {
    if (Array.isArray(farmsData?.farms)) return farmsData!.farms
    if (Array.isArray(farmsData)) return farmsData as any[]
    return []
  }, [farmsData])

  const [searchText, setSearchText] = useState('')
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([])
  const [placesService, setPlacesService] = useState<google.maps.places.AutocompleteService | null>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  const createWarehouseMutation = useMutateCreateWarehouse()
  const editWarehouseMutation = useMutateEditWarehouse()

  const lat = useWatch({ control, name: 'lat' })
  const lng = useWatch({ control, name: 'lng' })
  const hasLocation = typeof lat === 'number' && typeof lng === 'number'
  const fullAddress = useWatch({ control, name: 'full_address' })

  // Pre-fill when editing
  useEffect(() => {
    if (open && isEdit && warehouse) {
      reset({
        name: warehouse.name ?? '',
        farm_id: warehouse.farm_id ?? '',
        full_address: warehouse.full_address ?? '',
        lat: warehouse.lat ?? null,
        lng: warehouse.lng ?? null,
        province: '',
        district: '',
        subdistrict: '',
        postal_code: '',
      })
    } else if (open && !isEdit) {
      reset({ name: '', farm_id: '', full_address: '', lat: null, lng: null, province: '', district: '', subdistrict: '', postal_code: '' })
    }
  }, [open, isEdit, warehouse, reset])

  // Get user location on create
  useEffect(() => {
    if (!open || isEdit || hasLocation) return
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setValue('lat', pos.coords.latitude)
          setValue('lng', pos.coords.longitude)
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 60000, timeout: 10000 }
      )
    }
  }, [open, isEdit, hasLocation, setValue])

  const { data: rev } = useFarmsReverseGeocode(hasLocation ? lat! : 0, hasLocation ? lng! : 0)
  useEffect(() => {
    if (!rev || isEdit) return
    setValue('full_address', rev.full_address ?? '')
    setValue('province', rev.province ?? '')
    setValue('district', rev.district ?? '')
    setValue('subdistrict', rev.subdistrict ?? '')
    setValue('postal_code', rev.postal_code ?? '')
  }, [rev, setValue, isEdit])

  const { isLoaded } = useJsApiLoader(googleMapsConfig)
  useEffect(() => {
    if (isLoaded && window.google && !placesService) {
      setPlacesService(new window.google.maps.places.AutocompleteService())
    }
  }, [isLoaded, placesService])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchText(value)
    if (placesService && value) {
      placesService.getPlacePredictions(
        { input: value, componentRestrictions: { country: 'th' } },
        (predictions) => setSuggestions(predictions || [])
      )
    } else {
      setSuggestions([])
    }
  }

  const handleSuggestionClick = (s: google.maps.places.AutocompletePrediction) => {
    if (!window.google) return
    new window.google.maps.Geocoder().geocode({ placeId: s.place_id }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const loc = results[0].geometry?.location
        setValue('lat', loc?.lat() ?? 0)
        setValue('lng', loc?.lng() ?? 0)
        setSearchText(s.description)
        setSuggestions([])
      }
    })
  }

  const handleClose = () => {
    reset()
    setSearchText('')
    setSuggestions([])
    onClose()
  }

  const onSubmit = async (data: CreateWarehouseBody) => {
    setIsSubmitLoading(true)
    try {
      const body = {
        name: data.name,
        farm_id: data.farm_id === '' ? null : data.farm_id,
        full_address: data.full_address,
        lat: data.lat, lng: data.lng,
        province: data.province, district: data.district,
        subdistrict: data.subdistrict, postal_code: data.postal_code,
      }
      if (isEdit && warehouse) {
        await editWarehouseMutation.mutateAsync({ body, warehouseId: warehouse.id })
      } else {
        await createWarehouseMutation.mutateAsync(body)
      }
      handleClose()
    } finally {
      setIsSubmitLoading(false)
    }
  }

  return (
    <CustomDialog
      open={open}
      closeDialog={handleClose}
      title={isEdit ? t('warehouse.edit') : t('warehouse.create.new')}
      submitLabel={t('warehouse.save')}
      formName="warehouse-dialog-form"
      isSubmitLoading={isSubmitLoading}
      onSubmit={() => {}}
      enableCloseButton
      enableInLineWithTitle
      maxWidth="sm"
      fullWidth
      contentMinWidth={0}
    >
      <form id="warehouse-dialog-form" onSubmit={handleSubmit(onSubmit)}>
        {/* Map search */}
        <Box ref={searchContainerRef} sx={{ position: 'relative', mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            placeholder={t('warehouse.search.location')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              autoComplete: 'off',
            }}
          />
          {suggestions.length > 0 && (
            <List sx={{ position: 'absolute', zIndex: 1300, width: '100%', bgcolor: 'background.paper', boxShadow: 3, borderRadius: 1, maxHeight: 200, overflowY: 'auto' }}>
              {suggestions.map((s) => (
                <ListItem key={s.place_id} disablePadding>
                  <ListItemButton onClick={() => handleSuggestionClick(s)}>
                    <ListItemText primary={s.description} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Map */}
        <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <LocationMap
            value={hasLocation ? { lat: lat!, lng: lng! } : null}
            readOnly={false}
            height={280}
            defaultCenter={{ lat: 13.736717, lng: 100.523186 }}
            onChange={(p) => {
              setValue('lat', p.lat)
              setValue('lng', p.lng)
            }}
          />
        </Box>

        {/* ชื่อโกดัง */}
        <Box sx={{ mb: 2 }}>
          <label style={{ fontWeight: 600 }}>
            <span style={{ color: 'red', marginRight: 4 }}>*</span>
            {t('warehouse.name')}
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                sx={{ mt: 0.5 }}
                placeholder={t('warehouse.name.placeholder')}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Box>

        {/* ฟาร์ม */}
        <Box sx={{ mb: 2 }}>
          <label style={{ fontWeight: 600 }}>{t('warehouse.farm')}</label>
          <Controller
            name="farm_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                sx={{ mt: 0.5 }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) return <span style={{ opacity: 0.6 }}>{t('warehouse.farm.placeholder')}</span>
                    return farms.find((f: any) => String(f.id) === String(selected))?.name ?? ''
                  },
                }}
              >
                <MenuItem value=""><Box component="span" sx={{ color: 'text.secondary' }}>{t('warehouse.farm.placeholder')}</Box></MenuItem>
                {farms.map((f: any) => (
                  <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>

        {/* ที่อยู่ */}
        {fullAddress && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {t('warehouse.address')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fullAddress}
            </Typography>
          </Box>
        )}
      </form>
    </CustomDialog>
  )
}
